<template>
  <div class="project-single">
    <app-view-layout
      :header="project.name"
    >
      <template #toolbarMenu>
        <template v-if="project.softDeleted">
          <v-btn
            color="error"
            class="mr-2"
            @click="confirmPermanentDeleteProject"
          >
            Usuń trwale
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmBringingProjectBack"
          >
            Przywróć
          </v-btn>
        </template>

        <SingleProjectToolbarMenu
          v-else
          :project="project"
          :actions="actions"
          @print-report="printReport({id, name: project.name})"
          @delete="confirmProjectDelete"
        />
      </template>

      <template #content>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <ProjectThumbnailCard
              :image="project.thumbnail"
              :youtube="project.youtubeThumbnailUrl"
            />

            <ProjectDetailsCard
              class="mt-8"
              :project="project"
            />

            <ProjectChangesListCard
              v-if="project.history"
              class="mt-8"
              :history="project.history"
            />

            <ProjectChatCard
              v-if="project.chat"
              class="mt-8"
              :chat="project.chat"
              :project-id="id"
              :unread="haveUnreadMessages"
            />
          </v-col>

          <v-col
            cols="12"
            md="8"
          >
            <v-row>
              <v-col cols="12">
                <ProjectStatusCard
                  :project="project"
                  @changeStatus="showChangeStatusDialog"
                />
              </v-col>

              <v-col
                v-if="project.campaigns"
                cols="12"
                md="6"
              >
                <ProjectCampaignsCard :value="project.campaigns" />
              </v-col>
              <v-col
                v-if="project.tags"
                cols="12"
                md="6"
              >
                <ProjectTagsCard :value="project.tags" />
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="project.youtubeLink"
                cols="12"
              >
                <v-card class="pa-4">
                  <label class="label">Youtube url:</label>
                  <AppCopyUrl
                    input-id="youtubeLink"
                    :url="project.youtubeLink"
                  />
                </v-card>
              </v-col>

              <v-col
                v-if="project.hyperlink || project.discLocation"
                cols="12"
              >
                <ProjectAnchorsCard :project="project" />
              </v-col>

              <v-col>
                <ProjectTeamCard
                  :users="project.members"
                  title="Zespół projektowy"
                  :editable="teamEditPermitted"
                  @add="showAddTeamMemberDialog('members')"
                  @delete="deleteUser($event, 'members')"
                />
              </v-col>

              <v-col>
                <ProjectTeamCard
                  :users="project.orderers"
                  title="Zespół zlecający"
                  empty-state-text="Oczekiwanie na dodanie członków zespołu zlecającego."
                  :editable="teamEditPermitted"
                  @add="showAddTeamMemberDialog('orderers')"
                  @delete="deleteUser($event, 'orderers')"
                />
              </v-col>

              <v-col>
                <ProjectTeamCard
                  :users="project.managers"
                  :editable="teamEditPermitted"
                  title="Zespół decyzyjny"
                  empty-state-text="Oczekiwanie na dodanie członków zespołu decyzyjnego."
                  @add="showAddTeamMemberDialog('managers')"
                  @delete="deleteUser($event, 'managers')"
                />
              </v-col>

              <v-col
                cols="12"
                :order="user.layoutType === 'graphic_designer_layout' ? 'first': undefined"
              >
                <ProjectFilesCard
                  v-if="project.files"
                  ref="projectFiles"
                  :editable="project.editable && !project.softDeleted"
                  :disable-addition="!project.editable || project.softDeleted"
                  :disable-delete="!project.editable || project.softDeleted"
                  thumbnail
                  :files="project.files"
                  :processing="isProcessing"
                  @add="showFileUploadDialog"
                  @delete="deleteFiles"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <app-text-card
              v-if="project.description !== null"
              :value="project.description"
              title="Opis projektu"
              disabled
            >
              <AppEmptyState
                title="Brak opisu projektu"
                subtitle="Oczekiwanie na dodanie opisu projektu."
                class="pb-16"
              />
            </app-text-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <app-text-card
              v-if="project.copy !== null"
              :value="project.copy"
              title="Tekst do wykorzystania"
              disabled
            >
              <AppEmptyState
                title="Brak tekstu"
                subtitle="Oczekiwanie na dodanie tekstu do wykorzystania."
                class="pb-16"
              />
            </app-text-card>
          </v-col>
        </v-row>
      </template>
    </app-view-layout>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import AppViewLayout from '@/components/Layout/AppViewLayout.vue';
import SingleProjectToolbarMenu from '@/components/Projects/SingleProjectToolbarMenu.vue';
import ProjectThumbnailCard from '@/components/Projects/ProjectThumbnailCard.vue';
import ProjectDetailsCard from '@/components/Projects/ProjectDetailsCard.vue';
import ProjectTeamCard from '@/components/Projects/ProjectTeamCard.vue';
import ProjectChangesListCard from '@/components/Projects/Changes/ProjectChangesListCard.vue';
import ProjectChatCard from '@/components/Projects/ProjectChatCard.vue';
import ProjectFilesCard from '@/components/Projects/ProjectFilesCard.vue';
import ProjectTagsCard from '@/components/Projects/ProjectTagsCard.vue';
import ProjectStatusCard from '@/components/Projects/ProjectStatusCard.vue';
import ProjectCampaignsCard from '@/components/Projects/ProjectCampaignsCard.vue';
import ProjectAnchorsCard from '@/components/Projects/ProjectAnchorsCard.vue';
import AppTextCard from '@/components/UI/AppTextCard.vue';
import AppCopyUrl from '@/components/UI/AppCopyUrl.vue';
import AppEmptyState from '@/components/UI/AppEmptyState.vue';
import navigateBackMixin from '@/helpers/navigateBackMixin';

export default {
  components: {
    AppViewLayout,
    SingleProjectToolbarMenu,
    ProjectThumbnailCard,
    ProjectDetailsCard,
    ProjectTeamCard,
    ProjectChangesListCard,
    ProjectChatCard,
    ProjectFilesCard,
    ProjectTagsCard,
    ProjectStatusCard,
    ProjectCampaignsCard,
    ProjectAnchorsCard,
    AppTextCard,
    AppCopyUrl,
    AppEmptyState,
  },

  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },

  mixins: [navigateBackMixin], // prevRoute

  computed: {
    ...mapState('project', [
      'project',
      'isProcessing',
      'actions',
    ]),
    ...mapState('auth', [
      'user',
    ]),
    ...mapGetters('chat', [
      'projectChannelUnreadMessagesCount',
    ]),
    ...mapGetters('auth', [
      'checkUserPermissions',
    ]),

    teamEditPermitted() {
      const availability = ['admin_director', 'chief', 'user_admin'];
      const isNotSoftDeleted = !this.project.softDeleted;
      const isOwner = this.user.id === this.project.userId;
      const isValidUser = isOwner || this.checkUserPermissions(availability);
      return isNotSoftDeleted && isValidUser;
    },
    haveUnreadMessages() {
      return !!this.projectChannelUnreadMessagesCount(this.id);
    },
  },

  created() {
    this.fetchProject(this.id)
      .then(() => {
        this.fetchProjectActions(this.id);
      });
  },

  beforeDestroy() {
    this.resetProject();
  },

  methods: {
    ...mapActions('project', [
      'fetchProject',
      'deleteProjectFiles',
      'resetProject',
      'deleteProjectUser',
      'fetchProjectActions',
      'printReport',
    ]),

    ...mapActions('projects', [
      'softDeleteProject',
      'deleteProjectPermanently',
      'bringProjectBack',
    ]),

    ...mapActions('dialog', [
      'showDialog',
      'clearDialog',
    ]),

    deleteFiles(fileIds) {
      this.deleteProjectFiles(fileIds)
        .then(() => {
          this.$refs.projectFiles.clearSelection();
        });
    },

    deleteUser(userId, listType) {
      this.deleteProjectUser({ id: userId, listType });
    },

    confirmProjectDelete() {
      if (this.project.status === 'Wersja robocza') {
        this.confirmPermanentDeleteProject();
        } else {
        this.confirmSoftDeleteProject();
      }
    },

    callConfirmDialog(config, callback) {
      return new Promise((resolve) => {
        this.showDialog({
          component: 'Confirm',
          ...config,
        })
          .then(() => {
            callback(this.id).then(() => {
              resolve();
            });
          })
          .catch(() => {
            this.clearDialog();
          });
      });
    },
    confirmSoftDeleteProject() {
      const config = {
        header: 'Przenoszenie projektu do kosza',
        text: 'Czy na pewno chcesz przenieść projekt do kosza?',
        props: {
          confirmText: 'Przenieś',
          rejectText: 'Nie przenoś',
        },
      };
      this.callConfirmDialog(config, this.softDeleteProject);
    },

    confirmPermanentDeleteProject() {
      const config = {
        header: 'Trwałe usuwanie projektu',
        text: 'Czy na pewno chcesz trwale usunąć projekt?',
        props: {
          confirmText: 'Usuń',
          rejectText: 'Nie usuwaj',
        },
      };
      this.callConfirmDialog(config, this.deleteProjectPermanently)
        .then(() => {
          const { name } = this.prevRoute;
          this.$router.replace({ name });
        });
    },

    confirmBringingProjectBack() {
      const config = {
        header: 'Przywracanie projektu',
        text: 'Czy na pewno chcesz przywrócić ten projekt?',
        props: {
          confirmText: 'Przywróć',
          rejectText: 'Nie przywracaj',
        },
      };
      this.callConfirmDialog(config, this.bringProjectBack);
    },

    showFileUploadDialog() {
      this.showDialog({
        header: 'Dodawanie pliku',
        text: 'Wybierz plik i dodaj do projektu.',
        component: 'FileUpload',
      });
    },

    showAddTeamMemberDialog(teamType) {
      this.showDialog({
        header: 'Dodaj członka zespołu',
        text: 'W poniszym polu wybierz osobę której wysłać zaproszenie',
        component: 'AddProjectTeamMember',
        props: {
          type: teamType,
        },
      });
    },

    showChangeStatusDialog() {
      this.showDialog({
        header: 'Status projektu',
        text: 'Wybierz i zmień ręcznie status projektu',
        component: 'ProjectStatus',
        props: {
          id: this.id,
        },
      });
    },
  },
};
</script>
